import {
  PDF_FONT_SIZE,
  PDF_HEADER_BACKGROUND_COLOR,
  type ReportTableProps,
} from '@/components';
import {
  type ReportOrganizingTableFormat5Model,
  type ReportOrganizingTableFormat5PolicesModel,
  format5ColumnType,
  type projectDetailUrlTypeEnum,
} from '@/features/mold/models';
import {
  format5HeaderModifier,
  format5ValueConverter,
} from '@/features/mold/utils/organizing-table/format5/get-csv';
import { getProjectDetailPageUrl } from '@/features/project/_utils/project';
import { t } from '@/i18n';
import {
  formatProjectEndYearForPdf,
  formatProjectStartYearForPdf,
} from '@/utils';
import type { CSSProperties } from 'react';
import type { z } from 'zod';
import { centerTextStyle } from '../../pdf/pdf-utils';

type TableBodyElement = ReportTableProps['data'][number];

const headerStyle: CSSProperties = {
  fontSize: PDF_FONT_SIZE.MD,
  textAlign: 'center',
};

const MONEY_RATIO = 0.95;
const MONEY_STYLE: CSSProperties = { textAlign: 'right' };

export const getFormat5PdfHeader = ({
  year,
}: {
  year: number;
}): ReportTableProps['header'] => {
  const header = format5HeaderModifier({ currentYear: year });
  return [
    { value: header.policyLevel1, ratio: 0.3, textStyle: headerStyle },
    { value: header.policyLevel2, ratio: 0.3, textStyle: headerStyle },
    { value: header.projectNumber, ratio: 0.5, textStyle: headerStyle },
    { value: header.name, ratio: 1, textStyle: headerStyle },
    {
      value: header.parentOrganization,
      ratio: 1,
      textStyle: headerStyle,
    },
    { value: header.startYear, ratio: 0.5, textStyle: headerStyle },
    { value: header.endYear, ratio: 0.5, textStyle: headerStyle },
    { value: header.accountTitle, ratio: 1, textStyle: headerStyle },
    {
      value: header.majorExpenditureBudgetPurposeParent,
      ratio: 1,
      textStyle: headerStyle,
    },
    {
      value: header.majorExpenditureBudgetPurpose,
      ratio: 1,
      textStyle: headerStyle,
    },
    {
      type: 'multiple',
      ratio: MONEY_RATIO * 2,
      cell: {
        vertical: true,
        flexCells: [
          {
            value: header.priorUsableFinalizedAmountTotal,
            textStyle: headerStyle,
          },
          {
            type: 'multiple',
            cell: {
              flexCells: [
                { value: header.executableAmount, textStyle: headerStyle },
                { value: header.executionAmount, textStyle: headerStyle },
              ],
            },
          },
        ],
      },
    },
    {
      value: header.initialBudgetTotal,
      ratio: MONEY_RATIO,
      textStyle: headerStyle,
    },
    {
      value: header.skipAdministrativeReformTeamEvaluationReason,
      ratio: 1.5,
      textStyle: headerStyle,
    },
  ];
};

export const getForma5PdfBody = ({
  policies,
  isDebugMode,
  projectDetailUrlType,
}: {
  policies: Array<z.infer<typeof ReportOrganizingTableFormat5PolicesModel>>;
  isDebugMode: boolean;
  projectDetailUrlType?: z.infer<typeof projectDetailUrlTypeEnum> | null;
}): ReportTableProps['data'] => {
  return policies.flatMap<TableBodyElement>((policyLevel1) => {
    return [
      {
        rowStyle: { backgroundColor: PDF_HEADER_BACKGROUND_COLOR },
        row: [
          {
            value: policyLevel1.policyName ?? t('not-set'),
            colSpan: 18,
          },
        ],
      },
      ...policyLevel1.policyMeasuresData.flatMap<TableBodyElement>(
        (policyLevel2) => {
          return [
            {
              rowStyle: { backgroundColor: PDF_HEADER_BACKGROUND_COLOR },
              row: [
                { value: '' },
                {
                  value: policyLevel2.measureName ?? t('not-set'),
                  colSpan: 17,
                },
              ],
            },
            ...policyLevel2.projectsData.flatMap<TableBodyElement>(
              (content) => ({
                row: format5ColumnType.options.flatMap<
                  TableBodyElement['row'][number]
                >((key) => {
                  switch (key) {
                    case 'policyLevel1':
                    case 'policyLevel2': {
                      return { value: '' };
                    }
                    case 'name': {
                      return {
                        type: 'link',
                        href: getProjectDetailPageUrl({
                          isPS: projectDetailUrlType === 'public',
                          projectId: content.id,
                        }),
                        value: content.name,
                      };
                    }
                    case 'startYear': {
                      return {
                        value: formatProjectStartYearForPdf(content[key]),
                      };
                    }
                    case 'endYear': {
                      return {
                        value: formatProjectEndYearForPdf(content[key]),
                      };
                    }
                    case 'accountTitle': {
                      return {
                        value: format5ValueConverter.accountingType(
                          content.accountingType,
                          content.accountTitle,
                        ),
                      };
                    }
                    case 'parentOrganization': {
                      return {
                        value: content[key],
                        textStyle: {
                          display: '-webkit-box',
                          textOverflow: 'ellipsis',
                          overflow: 'hidden',
                          WebkitBoxOrient: 'vertical',
                          WebkitLineClamp: 3,
                          paddingTop: 0,
                          paddingBottom: 0,
                        },
                      };
                    }
                    case 'executionAmount':
                    case 'projectDetailPageUrl': {
                      return [];
                    }
                    case 'executableAmount': {
                      return {
                        type: 'multiple',
                        cell: {
                          flexCells: [
                            {
                              ...centerTextStyle,
                              value:
                                format5ValueConverter.previousFinalizedAmountTotal(
                                  {
                                    value: content.previousFinalizedAmountTotal,
                                    type: 'pdf',
                                    isDebugMode,
                                    negativeCount:
                                      content.negativePreviousFinalizedAmountCount,
                                    rawValue:
                                      content.rawPreviousFinalizedAmountTotal,
                                  },
                                ),
                              textStyle: MONEY_STYLE,
                            },
                            {
                              ...centerTextStyle,
                              value:
                                format5ValueConverter.previousExecutionAmountTotal(
                                  {
                                    value: content.previousExecutionAmountTotal,
                                    type: 'pdf',
                                    isDebugMode,
                                    negativeCount:
                                      content.negativePreviousYearExecutionAmountCount,
                                    rawValue:
                                      content.rawPreviousExecutionAmountTotal,
                                  },
                                ),
                              textStyle: MONEY_STYLE,
                            },
                          ],
                        },
                      };
                    }
                    case 'skipAdministrativeReformTeamEvaluationReason': {
                      return {
                        value:
                          format5ValueConverter.skipAdministrativeReformTeamEvaluationReason(
                            content.skipAdministrativeReformTeamEvaluationReason,
                          ),
                      };
                    }
                    default: {
                      const value = content[key];
                      const isInitialBudgetTotal = key === 'initialBudgetTotal';
                      return {
                        value: isInitialBudgetTotal
                          ? format5ValueConverter.initialBudgetTotal({
                              value: content.initialBudgetTotal,
                              type: 'pdf',
                              isDebugMode,
                              negativeCount: content.negativeInitialBudgetCount,
                              rawValue: content.rawInitialBudgetTotal,
                            })
                          : value,
                        textStyle: isInitialBudgetTotal
                          ? MONEY_STYLE
                          : undefined,
                      };
                    }
                  }
                }),
              }),
            ),
          ];
        },
      ),
    ];
  });
};

export const getFormat5PdfFooter = ({
  summary,
}: {
  summary: z.infer<typeof ReportOrganizingTableFormat5Model>['summary'];
}): ReportTableProps['footer'] => {
  const summaries = [...summary.general, ...summary.special];

  return summaries.map<TableBodyElement>((v, idx, arr) => {
    return {
      row: [
        ...(idx === 0
          ? [
              {
                rowSpan: arr.length,
                colSpan: 7,
                value: t('total'),
                textStyle: { textAlign: 'center' } satisfies CSSProperties,
              },
            ]
          : []),
        {
          colSpan: 3,
          value: format5ValueConverter.accountingType(
            v.accountingType,
            v.accountTitle,
          ),
          textStyle: { textAlign: 'center' },
        },
        {
          type: 'multiple',
          cell: {
            flexCells: [
              {
                value: format5ValueConverter.summaryPriorFinalizedAmountTotal({
                  values: v,
                  fileType: 'pdf',
                }),
                textStyle: MONEY_STYLE,
              },
              {
                value: format5ValueConverter.summaryPriorExecutionAmountTotal({
                  values: v,
                  fileType: 'pdf',
                }),
                textStyle: MONEY_STYLE,
              },
            ],
          },
        },
        {
          value: format5ValueConverter.summaryInitialBudgetTotal({
            values: v,
            fileType: 'pdf',
          }),
          textStyle: MONEY_STYLE,
        },
        {
          value: '',
        },
      ],
    };
  });
};
